/* ============================================================================
   olmia-hero.js — homepage hero product carousel. Ported from the design
   index.html inline script. Operates on the server-rendered .hero2__card nodes
   inside #heroTrack and builds its own clones for the seamless vertical loop.
   No global product-catalog dependency; prices are painted by olmia-ui.js.

   Editor-safe (see olmia-editor.js): init(root) is scoped to the section root and
   idempotent (clones are stripped before rebuilding, so a re-render never stacks
   duplicate clones); the auto-rotate interval and the window resize handler live
   at module scope and are cleared in teardown so nothing leaks. shopify:block:select
   pauses the carousel and shows the selected product's card.
   ========================================================================== */
(function () {
  const T = (k, f) => (window.olmiaT ? window.olmiaT(k, f) : f);
  // Module-scope resources for the single hero on the page, so teardown can find
  // them even after the section element is replaced by the editor.
  let heroTimer = null;
  let heroResize = null;
  let heroApi = null; // { pause, resume, showIndex } for block:select

  function teardown(sectionRoot) {
    if (heroTimer) { clearInterval(heroTimer); heroTimer = null; }
    if (heroResize) { window.removeEventListener('resize', heroResize); heroResize = null; }
    heroApi = null;
    const scope = sectionRoot || document;
    const track = scope.querySelector('#heroTrack');
    if (track) {
      // Strip previously-appended clones so a same-root re-init can't double them.
      track.querySelectorAll('.hero2__card[inert][aria-hidden="true"]').forEach(c => c.remove());
      track.style.transform = '';
      delete track.dataset.heroInit;
    }
  }

  function init(sectionRoot) {
    const scope = sectionRoot || document;
    const track = scope.querySelector('#heroTrack');
    const dots = scope.querySelector('#heroDots');
    if (!track || !dots) return;
    // Idempotent: if this exact track was already built, tear it down first.
    if (track.dataset.heroInit === '1') teardown(scope);

    const reals = [...track.children];
    const N = reals.length;
    if (!N) return;
    track.dataset.heroInit = '1';

    // MOBIEL (<=760px): één uitgelicht product, clean (geen carousel) — badge altijd "Uitgelicht"
    if (window.matchMedia('(max-width: 760px)').matches) {
      reals.slice(1).forEach(c => c.remove());
      const first = reals[0];
      const media = first.querySelector('.hero2__card-media');
      const b = first.querySelector('.hero2__card-badge');
      if (b) { b.className = 'hero2__card-badge badge badge--cyan'; b.textContent = T('collection.hero_featured', 'Uitgelicht'); }
      else if (media) { media.insertAdjacentHTML('afterbegin', '<span class="hero2__card-badge badge badge--cyan">' + T('collection.hero_featured', 'Uitgelicht') + '</span>'); }
      return;
    }

    const INTERVAL = 3500, TRANS = 700;
    // seq = [kloon(laatste), ...items, kloon(eerste 2)] → altijd peek boven én onder, naadloze loop
    const cloneLast = reals[N - 1].cloneNode(true);
    const cloneFirst = reals[0].cloneNode(true);
    const cloneSecond = reals[1 % N].cloneNode(true);
    // Each .hero2__card is a focusable <a>; the peek clones are visual duplicates
    // of real cards, so make them inert — removed from the focus order and the
    // accessibility tree (no duplicate tab stops / SR announcements, and no
    // aria-hidden-focus). inert has no visual effect, so the loop is unchanged.
    // (These inert+aria-hidden clones are also how teardown finds them to strip.)
    [cloneLast, cloneFirst, cloneSecond].forEach(c => {
      c.setAttribute('inert', '');
      c.setAttribute('aria-hidden', 'true');
    });
    track.insertBefore(cloneLast, reals[0]);
    track.appendChild(cloneFirst);
    track.appendChild(cloneSecond);
    const cards = [...track.children];

    dots.innerHTML = reals.map((_, i) => `<button class="hero2__dot" type="button" data-i="${i}" aria-label="Product ${i + 1}"></button>`).join('');
    const dotEls = [...dots.children];
    const slider = track.parentElement;
    let pos = 1 + Math.floor(N / 2); // start op de middelste echte kaart

    const place = () => {
      if (pos < 0 || pos >= cards.length) return;
      const sliderH = slider.getBoundingClientRect().height;
      const card = cards[pos];
      const y = sliderH / 2 - (card.offsetTop + card.offsetHeight / 2);
      track.style.transform = `translateY(${y}px)`;
      cards.forEach((c, i) => c.classList.toggle('is-active', i === pos));
      const real = ((pos - 1) % N + N) % N;
      dotEls.forEach((d, i) => d.classList.toggle('is-active', i === real));
    };
    const jump = () => { // onzichtbare sprong: track én kaarten bevriezen zodat niets opnieuw "inlaadt"
      track.style.transition = 'none';
      cards.forEach(c => c.style.transition = 'none');
      place();
      track.getBoundingClientRect();   // reflow → sprong gecommit zonder animatie
      track.style.transition = '';
      cards.forEach(c => c.style.transition = '');
    };
    requestAnimationFrame(jump); // begin-positie zonder animatie

    const advance = () => {
      pos++;
      track.style.transition = '';
      place();                                  // schuift met CSS-transitie
      // op de append-kloon (na de laatste echte kaart): ná de slide onzichtbaar terug naar de echte eerste
      if (pos === N + 1) setTimeout(() => { pos = 1; jump(); }, TRANS + 40);
    };
    // respecteer prefers-reduced-motion: geen auto-rotatie (deterministische begin-kaart)
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const start = () => { if (reduce) return; clearInterval(heroTimer); heroTimer = setInterval(advance, INTERVAL); };
    start();
    // hover pauzeert de carousel — per-node dedup guard (adversarial finding H1):
    // same-node repeat dispatches must not stack these listeners.
    if (slider.dataset.heroBound !== '1') {
      slider.dataset.heroBound = '1';
      slider.addEventListener('mouseenter', () => clearInterval(heroTimer));
      slider.addEventListener('mouseleave', start);
    }
    if (dots.dataset.heroBound !== '1') {
      dots.dataset.heroBound = '1';
      dots.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-i]'); if (!btn) return;
        pos = +btn.dataset.i + 1; track.style.transition = ''; place(); start();
      });
    }
    heroResize = () => jump();
    window.addEventListener('resize', heroResize, { passive: true });

    // Editor block:select hooks — pause the loop and bring the edited product forward.
    heroApi = {
      pause: () => clearInterval(heroTimer),
      resume: start,
      showIndex: (i) => { if (i < 0 || i >= N) return; pos = i + 1; track.style.transition = ''; place(); }
    };
  }

  // shopify:block:select — pause the carousel and jump to the selected product card.
  function blockSelect(sectionRoot, detail) {
    if (!heroApi) return;
    heroApi.pause();
    const scope = sectionRoot || document;
    const track = scope.querySelector('#heroTrack');
    if (track && detail && detail.blockId) {
      const reals = [...track.children].filter(c => !c.hasAttribute('inert'));
      const idx = reals.findIndex(c => (c.getAttribute('data-shopify-editor-block') || '').indexOf(detail.blockId) !== -1);
      if (idx !== -1) heroApi.showIndex(idx);
    }
  }
  function blockDeselect() { if (heroApi) heroApi.resume(); }

  if (window.OlmiaEditor) OlmiaEditor.register({
    key: 'hero-showcase',
    match: (r) => !!(r && r.querySelector && r.querySelector('#heroTrack')),
    init, teardown, blockSelect, blockDeselect
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
})();
